import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut, ShieldAlert, ArrowUpRight, LayoutDashboard, ClipboardList, Eye, Users, Building2, Link2, AlertCircle, Activity, History, Car, DollarSign, ShieldCheck, Clock, Receipt, Shield } from "lucide-react";
import Logo, { BrandLockup } from "@/components/Logo";
import Inbox from "@/components/admin/Inbox";
import WorkspaceCard from "@/components/admin/WorkspaceCard";
import GmailInbox from "@/components/admin/GmailInbox";
import PortalAdmin from "@/components/admin/PortalAdmin";
import { Shell } from "@/components/portal/opsui";
import { fetchMe, logout, startGoogleLogin } from "@/lib/auth";

const AdminIcons = { dashboard: LayoutDashboard, operations: ClipboardList, viewas: Eye, clients: Users, accounts: Building2, mappings: Link2, queue: AlertCircle, health: Activity, sync: History, drivers: Car, pricing: DollarSign, documents: ShieldCheck, policies: Clock, payroll: Receipt, security: Shield };

function Screen({ children, testId }) {
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-obsidian-900 px-6 py-24"
      data-testid={testId}
    >
      <div className="gold-frame w-full max-w-md rounded-3xl bg-obsidian-800 p-10 text-center">
        <BrandLockup className="mx-auto h-24 w-auto" />
        {children}
      </div>
    </main>
  );
}

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? null);
  const [status, setStatus] = useState(location.state?.user ? "ok" : "checking");
  const authError = location.state?.authError;

  useEffect(() => {
    if (status !== "checking") return;
    fetchMe()
      .then((u) => {
        setUser(u);
        setStatus("ok");
      })
      .catch(() => setStatus("anon"));
  }, [status]);

  const signOut = async () => {
    await logout();
    setUser(null);
    setStatus("anon");
  };

  if (status === "checking") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian-900" data-testid="admin-checking">
        <Logo size={56} />
      </main>
    );
  }

  if (status === "anon") {
    return (
      <Screen testId="admin-login">
        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Private area</p>
        <h1 className="mt-3 font-display text-3xl font-semibold text-ivory">Booking Inbox</h1>
        <p className="mt-3 text-sm text-ivory/60">
          Sign in with your Nanak Chauffeurs Google Workspace account.
        </p>
        {authError && (
          <p className="mt-4 text-sm text-red-400" data-testid="admin-auth-error">
            Sign-in could not be completed. Please try again.
          </p>
        )}
        <button
          onClick={startGoogleLogin}
          data-testid="admin-google-login-button"
          className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian-900 transition-all duration-300 hover:shadow-[0_0_40px_rgba(217,164,65,0.4)]"
        >
          Continue with Google
          <ArrowUpRight className="h-4 w-4" />
        </button>
      </Screen>
    );
  }

  if (!user?.is_admin) {
    return (
      <Screen testId="admin-forbidden">
        <ShieldAlert className="mx-auto mt-8 h-10 w-10 text-gold" />
        <h1 className="mt-4 font-display text-3xl font-semibold text-ivory">Not authorised</h1>
        <p className="mt-3 text-sm text-ivory/60">
          <span className="text-ivory" data-testid="admin-forbidden-email">{user?.email}</span> is not on the
          admin list for this site.
        </p>
        <button
          onClick={signOut}
          data-testid="admin-forbidden-logout"
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/40 px-7 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-obsidian-900"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </Screen>
    );
  }

  return <SuperAdminShell user={user} signOut={signOut} />;
}

const ADMIN_SECTIONS = [
  ["dashboard", "Booking Inbox", "LayoutDashboard"],
  ["operations", "Operations Board", "ClipboardList"],
  ["viewas", "View as Client", "Eye"],
  ["clients", "Customers", "Users"],
  ["accounts", "Corporate Accounts", "Building2"],
  ["mappings", "Account Mappings", "Link2"],
  ["queue", "Review Queue", "AlertCircle"],
  ["health", "Integration Health", "Activity"],
  ["sync", "Sync & Audit", "History"],
  ["drivers", "Drivers & Vehicles", "Car"],
  ["pricing", "Pricing Approvals", "DollarSign"],
  ["documents", "Insurance & Docs", "ShieldCheck"],
  ["policies", "Hour Policies", "Clock"],
  ["payroll", "Driver Hours & Payroll", "Receipt"],
  ["security", "Security Controls", "Shield"],
];
// Sections backed by the existing, functional PortalAdmin tabs:
const PORTAL_TABS = new Set(["operations", "viewas", "clients", "accounts", "mappings", "queue", "health", "sync"]);
const FUTURE_NOTES = {
  drivers: "Driver & vehicle roster management — managed in Nanak Operations / Corporate admin (Phase C).",
  pricing: "Corporate pricing proposals & approvals — Phase F.",
  documents: "Insurance & identification document management — Phase F.",
  policies: "Corporate driver-hour policy approvals — Phase D.",
  payroll: "Weekly driver-hours & payroll PDF reports — view & approve in Nanak Operations → Driver Hours & Payroll.",
  security: "Security controls & session management — in progress.",
};

function SuperAdminShell({ user, signOut }) {
  const [section, setSection] = useState("dashboard");
  const nav = ADMIN_SECTIONS.map(([key, label]) => [key, label, (AdminIcons[key] || AdminIcons.dashboard)]);
  return (
    <div className="h-screen bg-obsidian-900 font-sans text-ivory" data-testid="admin-inbox">
      <Shell portal="Super Admin" accentLabel="Diagnostics" nav={nav} active={section} onNav={setSection}
        rightSlot={<div className="flex items-center gap-3">
          <a href="/operations" data-testid="open-operations-portal-button" className="hidden items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light hover:bg-gold/20 sm:inline-flex"><ArrowUpRight className="h-3.5 w-3.5" /> Open Nanak Operations</a>
          <span className="hidden font-mono text-[11px] text-ivory/50 md:inline" data-testid="admin-user-email">{user.email}</span>
          <button onClick={signOut} data-testid="admin-logout-button" className="rounded-full border border-gold/30 p-1.5 text-gold-light hover:bg-gold/10"><LogOut className="h-3.5 w-3.5" /></button>
        </div>}>
        <div className="p-6">
          {section === "dashboard" ? (
            <div className="space-y-6">
              <div><p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Private · Super Admin</p><h1 className="mt-2 font-display text-3xl font-semibold text-ivory">Welcome back, <em className="gold-text italic">{user.name?.split(" ")[0] || "Harry"}.</em></h1></div>
              <div className="grid gap-6 lg:grid-cols-12"><div className="lg:col-span-8"><Inbox /></div><div className="lg:col-span-4"><WorkspaceCard /></div></div>
              <GmailInbox />
            </div>
          ) : PORTAL_TABS.has(section) ? (
            <PortalAdmin tab={section} onTab={setSection} />
          ) : (
            <div className="flex min-h-[520px] flex-col items-center justify-center rounded-2xl border border-gold/15 bg-obsidian-800 text-center" data-testid={`admin-future-${section}`}>
              <ShieldAlert className="h-8 w-8 text-gold-light" />
              <p className="mt-4 text-lg font-semibold text-ivory">{ADMIN_SECTIONS.find((s) => s[0] === section)[1]}</p>
              <p className="mt-2 max-w-sm text-sm text-muted-steel">{FUTURE_NOTES[section]}</p>
            </div>
          )}
        </div>
      </Shell>
    </div>
  );
}
